import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  LogIn,
} from "lucide-react";
import { SITE_LOGO } from "../../services/ImageService";
import { InfoService } from "../../services/site/InfoService";

// Header Component
const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const products = [
    { name: "WhatsApp Ordering (HDT Booking)", href: "/whatsapp-ordering", isNew: true },
    { name: "AI Data Extraction", href: "/ai-data-extraction" },
    { name: "Intelligent Chatbot", href: "/intelligent-chatbot" },
    { name: "Digital Signature", href: "/digital-signature-solution" },
    { name: "PDF/Word Templates", href: "/pdf-word-template-filling" },
    { name: "Content Security Policy", href: "/content-security-policy" },
    { name: "Employee Onboarding", href: "/employee-onboarding" },
    { name: "Client Onboarding", href: "/client-onboarding" },
    { name: "Vendor Onboarding", href: "/vendor-onboarding" },
  ];

  const services = [
    { name: "Services Overview", href: "/services" },
    { name: "Application Development", href: "/services/application-development" },
    { name: "Staffing Solutions", href: "/services/staffing-solutions" },
  ];

  return (
    <header className="header">
      <div className="container">
        <nav className="nav">
          <motion.div
            className="logo"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div>
              <img src={SITE_LOGO} alt="HDT Solutions" />
            </div>
            {/* <div className="logo-icon">HDT</div>
            <div className="logo-text">
              <span className="logo-name">HDT Solutions</span>
              <span className="logo-tagline">Transforming Ideas into Digital Reality</span>
            </div> */}
          </motion.div>

          {/* Desktop Menu */}
          <ul className="nav-menu desktop-menu">
            <li>
              <a href="/" className="nav-link">
                Home
              </a>
            </li>
           <li className="dropdown is-active">
              <button
                className="nav-link dropdown-toggle"
                onMouseEnter={() => setIsProductsOpen(true)}
                // onMouseLeave={() => setIsProductsOpen(false)}
              >
                Products <ChevronDown size={16} />
              </button>
              {isProductsOpen && (
                <motion.div
                  id="dropdown-menu"
                  role="menu"
                  className="dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onMouseEnter={() => setIsProductsOpen(true)}
                  onMouseLeave={() => setIsProductsOpen(false)}
                >
                  {products.map((product, idx) => (
                    <a key={idx} href={product.href} className="dropdown-item">
                      {product.name}
                      {product.isNew && (
                        <span className="header-new-badge">NEW</span>
                      )}
                    </a>
                  ))}
                </motion.div>
              )}
            </li>
            <li className="dropdown is-active">
              <button
                className="nav-link dropdown-toggle"
                onMouseEnter={() => setIsServicesOpen(true)}
              >
                Services <ChevronDown size={16} />
              </button>
              {isServicesOpen && (
                <motion.div
                  id="dropdown-menu"
                  role="menu"
                  className="dropdown-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  {services.map((service, idx) => (
                    <a key={idx} href={service.href} className="dropdown-item">
                      {service.name}
                    </a>
                  ))}
                </motion.div>
              )}
            </li>
            <li>
              <a href="/about-us" className="nav-link">
                About Us
              </a>
            </li>
            <li>
              <a href="/pricing" className="nav-link">
                Pricing
              </a>
            </li>
            <li>
              <a href="/contact-us" className="nav-link">
                Contact Us
              </a>
            </li>
            <li>
              <a href={InfoService.loginLink} className="login-btn">
                <LogIn size={18} />
                <span>Login</span>
              </a>
            </li>
          </ul>

          {/* Login Button - Mobile */}
          <a href={InfoService.loginLink} className="login-btn-mobile">
            <LogIn size={20} />
          </a>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
          >
            <a href="/" className="mobile-link">
              Home
            </a>
            <div className="mobile-dropdown">
              <button
                onClick={() => setIsProductsOpen(!isProductsOpen)}
                className="mobile-link"
              >
                Products <ChevronDown size={16} />
              </button>
              {isProductsOpen && (
                <div className="mobile-submenu">
                  {products.map((product, idx) => (
                    <a
                      key={idx}
                      href={product.href}
                      className="mobile-link submenu-item"
                    >
                      {product.name}
                      {product.isNew && (
                        <span className="header-new-badge">NEW</span>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="mobile-dropdown">
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="mobile-link"
              >
                Services <ChevronDown size={16} />
              </button>
              {isServicesOpen && (
                <div className="mobile-submenu">
                  {services.map((service, idx) => (
                    <a
                      key={idx}
                      href={service.href}
                      className="mobile-link submenu-item"
                    >
                      {service.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <a href="/about-us" className="mobile-link">
              About Us
            </a>
            <a href="/pricing" className="mobile-link">
              Pricing
            </a>
            <a href="/contact-us" className="mobile-link">
              Contact Us
            </a>
            <a href={InfoService.loginLink} className="mobile-link login-link-mobile">
              <LogIn size={18} />
              <span>Login</span>
            </a>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;
